import * as React from "react";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { SnapshotEventMail } from "@/modules/snapshots/businessReports.types";
import { sanitizeSnapshotEventHtml } from "@/modules/snapshots/snapshotEvents/sanitize";
import { NoteListItem } from "./NoteListItem";

interface Props {
  notes: SnapshotEventMail[];
  isLoading: boolean;
  selectedNoteId: string | null;
  isCreatingNew: boolean;
  onSelectNote: (note: SnapshotEventMail) => void;
  onNewNote: () => void;
}

export const NotesSidebar = ({ notes, isLoading, selectedNoteId, isCreatingNew, onSelectNote, onNewNote }: Props) => {
  const [searchQuery, setSearchQuery] = React.useState("");

  const sanitizedMessages = React.useMemo(
    () => new Map(notes.map(n => [n.id, sanitizeSnapshotEventHtml(n.message)])),
    [notes],
  );

  const filteredNotes = React.useMemo(() => {
    const q = searchQuery.toLowerCase();
    return notes.filter(note => note.title.toLowerCase().includes(q) || note.message.toLowerCase().includes(q));
  }, [notes, searchQuery]);

  return (
    <div className="flex w-1/3 flex-col gap-4 border-r border-gray-200 bg-ivory-mist-200 p-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 z-10 -translate-y-1/2 text-gray-400" size={18} />
        <Input
          type="text"
          placeholder="Search notes..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      <div className="flex max-h-[40vh] flex-col gap-2 overflow-y-auto">
        {isLoading && <div className="p-4 text-center text-gray-500">Loading...</div>}
        {!isLoading && filteredNotes.length === 0 && (
          <div className="p-4 text-center text-gray-500">No notes found</div>
        )}
        {filteredNotes.map(note => (
          <NoteListItem
            key={note.id}
            note={note}
            isSelected={selectedNoteId === note.id && !isCreatingNew}
            sanitizedMessage={sanitizedMessages.get(note.id) ?? ""}
            onSelect={onSelectNote}
          />
        ))}
      </div>

      <Button type="button" variant="outline" onClick={onNewNote} className="mt-auto w-full">
        + New note
      </Button>
    </div>
  );
};
